import { useCallback, useEffect, useMemo, useState } from "react";
import type { LabelPrintFormat, LabelPrintFormatGroup, LabelPrintMarginSettings } from "@itaas/label-print";
import { getGroupedLabelPrintFormats, getLabelPrintFormat, labelPrintSizeHint } from "@/core/printing/label-print-formats";
import {
  loadLabelPrintUserSettings,
  mergeUserMarginPatch,
  resolveSettingsForPrinter,
  saveLabelPrintUserSettings,
  type LabelPrintUserSettings,
} from "@/core/printing/label-print-settings";
import type { LabelPrintRotation } from "@/core/printing/label-print-rotation";
import { saveLabelRotation } from "@/core/printing/label-print-rotation";
import {
  fetchPrintBridgeFormats,
  savePrintBridgePrinterProfile,
} from "@/core/printing/print-bridge-client";
import { LabelPrintBridgeButton } from "@/components/supply/LabelPrintBridgeButton";
import { LabelFormatControl, LabelRotationControl } from "@/components/supply/LabelPrintPreview";
import { LabelMarginControl } from "@/components/printing/LabelMarginControl";
import { PrintBridgeAgentDownload } from "@/components/printing/PrintBridgeAgentDownload";
import type { ProductLabelData } from "@/components/supply/ProductLabelPrintSheet.types";

export interface LabelPrintSettingsPanelProps {
  label: ProductLabelData | null;
  disabled?: boolean;
  compact?: boolean;
  showAgentDownload?: boolean;
  onPrinted?: () => void;
  onPrintingChange?: (printing: boolean) => void;
  onError?: (message: string | null) => void;
  onSuccess?: (message: string) => void;
  onSettingsChange?: (settings: LabelPrintUserSettings) => void;
}

function buildFormatGroups(
  formats: LabelPrintFormat[],
  apiGroups?: LabelPrintFormatGroup[],
): Array<LabelPrintFormatGroup & { formats: LabelPrintFormat[] }> {
  const groups = apiGroups?.length ? apiGroups : [];
  if (!groups.length) {
    return getGroupedLabelPrintFormats();
  }
  return groups
    .map((group) => ({
      ...group,
      formats: formats.filter((f) => f.group === group.id),
    }))
    .filter((g) => g.formats.length > 0);
}

export function LabelPrintSettingsPanel({
  label,
  disabled = false,
  compact = false,
  showAgentDownload = true,
  onPrinted,
  onPrintingChange,
  onError,
  onSuccess,
  onSettingsChange,
}: LabelPrintSettingsPanelProps) {
  const [settings, setSettings] = useState<LabelPrintUserSettings>(() => loadLabelPrintUserSettings());
  const [formatGroups, setFormatGroups] = useState<
    Array<LabelPrintFormatGroup & { formats: LabelPrintFormat[] }>
  >(() => getGroupedLabelPrintFormats());
  const [bridgeConnected, setBridgeConnected] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState("");
  const [printerProfiles, setPrinterProfiles] = useState<
    Record<string, import("@itaas/label-print").LabelPrinterProfile>
  >({});

  const format = useMemo(
    () => getLabelPrintFormat(settings.formatId) ?? getLabelPrintFormat("60x40-180")!,
    [settings.formatId],
  );

  const rotation = settings.rotation;

  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    void fetchPrintBridgeFormats()
      .then((data) => {
        setFormatGroups(buildFormatGroups(data.formats, data.groups as LabelPrintFormatGroup[]));
      })
      .catch(() => {
        setFormatGroups(getGroupedLabelPrintFormats());
      });
  }, []);

  const applySettings = useCallback(
    (next: LabelPrintUserSettings) => {
      setSettings(next);
      saveLabelPrintUserSettings(next);
      saveLabelRotation(next.rotation);
      onSettingsChange?.(next);
    },
    [onSettingsChange],
  );

  const handleFormatChange = (formatId: string) => {
    applySettings({ ...settings, formatId });
  };

  const handleRotationChange = (nextRotation: LabelPrintRotation) => {
    applySettings({ ...settings, rotation: nextRotation });
  };

  const handleMarginPatch = (patch: Partial<LabelPrintMarginSettings>) => {
    applySettings({
      ...settings,
      margins: mergeUserMarginPatch(settings.margins, patch),
    });
  };

  const handlePrinterChange = (printerName: string, driverName?: string) => {
    setSelectedPrinter(printerName);
    const resolved = resolveSettingsForPrinter(
      printerName,
      driverName,
      printerProfiles,
      settings,
    );
    applySettings(resolved);
  };

  const handleSaveProfile = async () => {
    if (!selectedPrinter) return;
    setSavingProfile(true);
    onError?.(null);
    try {
      await savePrintBridgePrinterProfile({
        printerName: selectedPrinter,
        formatId: settings.formatId,
        rotation: settings.rotation,
        margins: settings.margins,
      });
      onSuccess?.(`Profil enregistré pour ${selectedPrinter}.`);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : "Enregistrement échoué.");
    } finally {
      setSavingProfile(false);
    }
  };

  const showRollFields = Boolean(format.rollWidthMm ?? settings.margins.rollWidthMm);

  return (
    <div className="d-flex flex-column gap-2">
      {formatGroups.length > 0 ? (
        <DynamicLabelFormatControl
          formatId={settings.formatId}
          groups={formatGroups}
          compact={compact}
          onChange={handleFormatChange}
        />
      ) : (
        <LabelFormatControl
          formatId={settings.formatId}
          compact={compact}
          onChange={handleFormatChange}
        />
      )}

      <p className="small text-muted mb-0">{labelPrintSizeHint(format)}</p>

      <LabelRotationControl
        rotation={rotation}
        compact={compact}
        onChange={handleRotationChange}
      />

      <LabelMarginControl
        margins={settings.margins}
        showRollFields={showRollFields}
        onChange={handleMarginPatch}
        onSaveProfile={selectedPrinter ? () => void handleSaveProfile() : undefined}
        savingProfile={savingProfile}
      />

      <LabelPrintBridgeButton
        label={label}
        format={format}
        rotation={rotation}
        margins={settings.margins}
        disabled={disabled}
        onPrinted={() => onPrinted?.()}
        onPrintingChange={onPrintingChange}
        onError={onError}
        onSuccess={onSuccess}
        onFormatChange={handleFormatChange}
        onPrinterChange={handlePrinterChange}
        onBridgeStateChange={(state) => {
          setBridgeConnected(state.connected);
          setPrinterProfiles(state.printerProfiles ?? {});
          if (state.selectedPrinter && !selectedPrinter) {
            setSelectedPrinter(state.selectedPrinter);
          }
        }}
      />

      {showAgentDownload && (
        <PrintBridgeAgentDownload bridgeConnected={bridgeConnected} compact={compact} />
      )}
    </div>
  );
}

function DynamicLabelFormatControl({
  formatId,
  groups,
  onChange,
  compact,
}: {
  formatId: string;
  groups: Array<LabelPrintFormatGroup & { formats: LabelPrintFormat[] }>;
  onChange: (formatId: string) => void;
  compact?: boolean;
}) {
  return (
    <div className={`ph-label-format-control${compact ? " ph-label-format-control--compact" : ""}`}>
      {!compact && (
        <span className="ph-label-format-control-label small fw-semibold">
          Format imprimante
        </span>
      )}
      <select
        className="form-select form-select-sm"
        value={formatId}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Format papier étiquette"
      >
        {groups.map((group) => (
          <optgroup key={group.id} label={group.label}>
            {group.formats.map((format) => (
              <option key={format.id} value={format.id}>
                {format.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

export function useLabelPrintSettings(): LabelPrintUserSettings {
  return loadLabelPrintUserSettings();
}
